import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import cogoToast from 'cogo-toast';
import moment from 'moment';
import { CiEdit } from 'react-icons/ci';
import Flatpickr from 'react-flatpickr';
import Select from 'react-select';
import { Badge, Button, Card, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';

import deleteIcon from '../../../../../../../assets/images/delete.png';
import golfUser from '../../../../../../../assets/images/golf-user.png';
import { GET_REASON_BY_ACTIONS, MODIFY_BOOKING, MODIFY_BOOKING_EMPTY } from '../../../../../../../store/actions';
import FromAndToRangePicker from './Components/FromAndToRangePicker/FromAndToRangePicker';

const ModifyPlayerV2 = ({ onClose, currentSelectedBooking, bookingDetails, setIsShowModifyOptions }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({});
  const [errors, setErrors] = useState({});
  const [selectedRemark, setSelectedRemark] = useState(null);
  const [initialState, setInitialState] = useState({});
  const [isShowMaxPassengersCountModal, setIsShowMaxPassengersCountModal] = useState(false);

  /* ---------------------------- REDUX STATES ---------------------------- */
  const { modifyBooking, modifyBookingLoading, showModifyField, showModifyFieldLoading, remarksList, remarksListLoading } = useSelector(state => ({
    modifyBooking: state?.ModifyBooking?.data,
    modifyBookingLoading: state?.ModifyBooking?.loading,

    showModifyField: state?.ShowModifyField?.data,
    showModifyFieldLoading: state?.ShowModifyField?.loading,

    remarksList: state?.GetReasonByActions?.data,
    remarksListLoading: state?.GetReasonByActions?.loading,
  }));

  useEffect(() => {
    if (showModifyField?.fields && currentSelectedBooking) {
      const newState = showModifyField.fields.reduce((acc, field) => {
        if (field?.key === 'service_date') {
          const [date] = currentSelectedBooking?.serviceDateTime?.split(' ');
          acc[field.key] = moment(date, 'DD/MM/YYYY')?.format('YYYY-MM-DD');
        } else if (field?.key === 'service_time') {
          const [, time, day] = currentSelectedBooking?.serviceDateTime?.split(' ');
          acc[field.key] = moment(`${time} ${day}`, 'h:mm A').format('HH:mm');
        } else if (field?.key === 'players') {
          acc[field.key] = bookingDetails?.bookingDetails?.players || '';
        } else {
          acc[field.key] = currentSelectedBooking?.[field?.key] || '';
        }
        return acc;
      }, {});
      setState(newState);
      setInitialState(newState); //
    }
  }, [showModifyField, currentSelectedBooking]);

  useEffect(() => {
    dispatch({
      type: GET_REASON_BY_ACTIONS,
      payload: {
        urlParams: {
          booking_type: currentSelectedBooking?.bookingType || '',
          booking_status: currentSelectedBooking?.bookingStatus,
          action: 'modify_booking',
          is_active: true,
          page_size: 50,
          page_index: 0,
        },
      },
    });
  }, [currentSelectedBooking]);

  const handleChange = (key, value) => {
    setState(prevState => ({ ...prevState, [key]: value }));
    setErrors(prevErrors => ({ ...prevErrors, [key]: '' }));
  };

  const validateForm = () => {
    let newErrors = {};

    showModifyField?.fields?.forEach(field => {
      const value = state[field.key];

      if (!value) {
        newErrors[field.key] = `${field.name} is required`;
      }

      // Validate number fields
      if (field.type === 'number' && value !== '' && (value < 0 || value > field.max)) {
        newErrors[field.key] = `Value must be between 0 and ${field.max}`;
      }

      if (field.type === 'date' && value !== '' && moment(value).isBefore(moment(field.from))) {
        newErrors[field.key] = `The date must be between ${field.from} and ${field.to}.`;
      }
    });

    if (!selectedRemark) {
      newErrors['reason_id'] = 'Remarks are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      let updatedFields = Object.keys(state).reduce((acc, key) => {
        if (state[key] !== initialState[key]) {
          acc[key] = state[key];
        }
        return acc;
      }, {});

      if (
        Number(state?.number_of_passengers) + Number(state?.number_of_baby_seats) + Number(state?.number_of_infants) >
        showModifyField.fields?.find(field => field?.key === 'number_of_passengers')?.max
      ) {
        setIsShowMaxPassengersCountModal(true);
        return;
      }

      if (Object.keys(updatedFields).length === 0) {
        cogoToast.error('No changes detected. Please make the necessary modifications to the booking.');
        return;
      }

      updatedFields = {
        ...updatedFields,
        ...(updatedFields?.service_date && { service_time: state?.service_time }),
        ...(updatedFields?.service_time && { service_date: state?.service_date }),
      };

      const payloadData = {
        ...updatedFields,
        booking_id: currentSelectedBooking?.id,
        reason_id: selectedRemark?.value,
        guests: state?.players,
        ...(state?.preferred_date_time_1 || {}),
      };

      if (payloadData?.preferred_date_time_1) {
        delete payloadData?.preferred_date_time_1;
      }

      dispatch({
        type: MODIFY_BOOKING,
        payload: {
          body: {
            ...payloadData,
          },
        },
      });
    }
  };

  const removeGuest = (key, index) => {
    const updatedGuests = [...state[key]];
    updatedGuests.splice(index, 1);

    handleChange(key, updatedGuests);
  };

  useEffect(() => {
    if (modifyBooking !== null) {
      dispatch({
        type: MODIFY_BOOKING_EMPTY,
      });
      onClose();
      setIsShowModifyOptions(false);
    }

    return () => {
      dispatch({
        type: MODIFY_BOOKING_EMPTY,
      });
    };
  }, [modifyBooking]);

  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    handleChange(`preferred_date_time_1`, {
      preferred_date_time1_range_from: `${selectedSlot?.time}`,
      preferred_date_time1_range_to: `${selectedSlot?.toTime}`,
    });
  }, [selectedSlot]);

  const [slotsDataObject, setSlotsDataObject] = useState(null);
  const [transformedSlotsData, setTransformedSlotsData] = useState([]);
  const [intervalData, setIntervalData] = useState(0);

  const [isShowModifyPreferredSlot, setIsShowModifyPreferredSlot] = useState(false);

  function convertToFromTimeObjects(timeArray) {
    return timeArray?.map(time => ({ fromTime: time }));
  }

  useEffect(() => {
    if (showModifyField?.fields?.length > 0) {
      const filteredSlotsData = showModifyField?.fields?.filter(field => field?.type === 'select' && field?.options?.length > 0);

      if (filteredSlotsData?.length > 0) {
        setSlotsDataObject(filteredSlotsData?.[0]);
        setTransformedSlotsData(convertToFromTimeObjects(filteredSlotsData?.[0]?.['options']));
        setIntervalData(filteredSlotsData?.[0]?.['interval'] ?? 0);

        // setSelectedSlot({ time: filteredSlotsData?.[0]?.['preferred_date_time1_range_from'] });
        setSelectedSlot({
          time: `${filteredSlotsData?.[0]?.['preferred_date_time1_range_from']}`,
          toTime: `${moment(`${filteredSlotsData?.[0]?.['preferred_date_time1_range_from']}`, 'HH:mm')
            .add(filteredSlotsData?.[0]?.['interval'], 'minutes')
            .format('HH:mm')}`,
        });
      }
    }
  }, [showModifyField?.fields]);

  return (
    <Modal isOpen={true} centered size="lg">
      <ModalHeader toggle={onClose}>
        <h4>Modify Booking </h4>
      </ModalHeader>

      <ModalBody>
        <Row>
          {Object.keys(state).length > 0 && Array.isArray(showModifyField?.fields) && showModifyField?.fields?.length > 0 && (
            <>
              {showModifyField?.fields?.map(field => {
                if (field.type === 'players') {
                  return (
                    <Row key={field.key}>
                      {Array.isArray(state?.players) &&
                        state.players.map((player, index) => (
                          <Col md="6" className="mb-3" key={player?.id || index}>
                            <Card body style={{ backgroundColor: '#EAEDF5' }}>
                              <Row className="d-flex align-items-center">
                                <Col xs="3">
                                  <img src={golfUser} alt={player?.name} style={{ width: '52px' }} />
                                </Col>
                                <Col xs="7">
                                  <div className="d-flex align-items-center gap-2">
                                    <h5 className="fw-semibold m-0">{player?.name}</h5>
                                    <Badge pill className="text-white" style={{ backgroundColor: '#405189 !important' }}>
                                      {player?.player_type}
                                    </Badge>
                                  </div>
                                </Col>
                                {player?.player_type === 'Guest' && (
                                  <Col xs="2" className="text-right">
                                    <img
                                      src={deleteIcon}
                                      className="cursor-pointer"
                                      style={{ width: '22px' }}
                                      onClick={() => removeGuest(field.key, index)}
                                    />
                                  </Col>
                                )}
                              </Row>
                            </Card>
                          </Col>
                        ))}
                    </Row>
                  );
                }

                return (
                  <Col key={field.key} md={6} className="mb-3">
                    {(() => {
                      if (field.type === 'date') {
                        return (
                          <div className="area_expandable">
                            <Label className="form-label">{field.name}</Label>
                            <Flatpickr
                              className={`form-control ${field?.is_disabled && 'disabled-date'}`}
                              options={{
                                dateFormat: 'Y-m-d',
                                mode: 'single',
                                minDate: state[field.key] && moment(state[field.key]).isBefore(moment(field.from)) ? state[field.key] : field.from,
                                maxDate: field.to,
                              }}
                              value={state?.[field?.key]}
                              disabled={field?.is_disabled}
                              onChange={date => handleChange(field.key, moment(date[0]).format('YYYY-MM-DD'))}
                            />
                            <span className="icon_calendar_v2_golf">
                              <i className="ri-calendar-2-line"></i>
                            </span>
                          </div>
                        );
                      }

                      if (field.type === 'select') {
                        return (
                          <>
                            <Label className="form-label">{field.name}</Label>
                            <div
                              className="dummy-input"
                              onClick={() => {
                                setIsShowModifyPreferredSlot(true);
                              }}
                            >
                              <span>{`${moment(selectedSlot?.time, 'HH:mm').format('hh:mm A')} - ${moment(selectedSlot?.toTime, 'HH:mm').format(
                                'hh:mm A',
                              )}`}</span>
                              <span>
                                <CiEdit />
                              </span>
                            </div>

                            {/* <Select
                              options={options}
                              value={options.find(val => val.value === state?.[field?.key])}
                              onChange={data => handleChange(field.key, data?.value)}
                            /> */}
                            {/* <TimePicker
                              className="w-100"
                              use12Hours
                              format="h:mm a"
                              disabledHours={disabledHours}
                              disabledMinutes={disabledMinutes}
                              value={selectedSpecificSlotTime}
                              onChange={time => {
                                setSelectedSpecificSlotTime(time);
                                handleChange(field.key, time.format('HH:mm'));
                              }}
                            /> */}

                            {/* <Col md="6" className="mb-3">
                              <Label className="form-label">{'Preferred Slot 1'}</Label>
                              <div className="select-slot-container">
                                <div className="slot-range-picker-container">
                                  <FromAndToRangePicker
                                    slotData={timeSlotList}
                                    selectedSlot={selectedSlot}
                                    setSelectedSlot={setSelectedSlot}
                                    selectedDate={selectedDate}
                                  />
                                </div>
                              </div>
                            </Col> */}
                          </>
                        );
                      }

                      if (field.type === 'time') {
                        return (
                          <>
                            <Label className="form-label">{field.name}</Label>
                            <Input
                              type="number"
                              value={state?.[field?.key]}
                              onChange={e => {
                                let value = e.target.value.replace(/^0+/, '') || '0';
                                value = parseInt(value, 10);
                                if (isNaN(value)) value = '';
                                handleChange(field.key, String(value));

                                if (value < 0 || value > field.max) {
                                  setErrors(prevErrors => ({
                                    ...prevErrors,
                                    [field.key]: `Value must be between 0 and ${field.max}`,
                                  }));
                                } else {
                                  setErrors(prevErrors => ({
                                    ...prevErrors,
                                    [field.key]: '',
                                  }));
                                }
                              }}
                            />
                          </>
                        );
                      }

                      return null;
                    })()}
                    {errors[field.key] && <div className="text-danger">{errors[field.key]}</div>}
                  </Col>
                );
              })}
            </>
          )}

          {/* {slotsDataObject && (
            <Col md="12" className="mb-3">
              <Label className="form-label">{'Preferred Slot 1'}</Label>
              <div className="select-slot-container">
                <div className="slot-range-picker-container">
                  <FromAndToRangePicker
                    slotData={transformedSlotsData}
                    selectedSlot={selectedSlot}
                    setSelectedSlot={setSelectedSlot}
                    selectedDate={''}
                  />
                </div>
              </div>
            </Col>
          )} */}
          {isShowModifyPreferredSlot && (
            <ModifyPreferredSlot
              isOpen={isShowModifyPreferredSlot}
              onClose={() => setIsShowModifyPreferredSlot(false)}
              slotsDataObject={slotsDataObject}
              transformedSlotsData={transformedSlotsData}
              selectedSlot={selectedSlot}
              setSelectedSlot={setSelectedSlot}
              intervalData={intervalData}
            />
          )}

          <Col md={12} className="mb-3">
            <Label htmlFor="name" className="form-label">
              Remarks
            </Label>
            <Select
              id="decline-remarks"
              name="decline-remarks"
              placeholder="Select reason for Modify the booking"
              value={selectedRemark}
              options={
                remarksList?.reasons?.map(remark => ({
                  label: `${remark?.reason}`,
                  value: `${remark?.id}`,
                })) || []
              }
              onChange={data => {
                setSelectedRemark(data);
                setErrors(prevErrors => ({ ...prevErrors, reason_id: '' }));
              }}
            />
            {errors['reason_id'] && <div className="text-danger">{errors['reason_id']}</div>}
          </Col>
        </Row>

        <Col md={12} className="mt-3 mb-4 text-center">
          {modifyBookingLoading ? (
            <Button color="primary" style={{ fontSize: '14px', width: '200px' }}>
              <Spinner className="btn-replacer-spinner" />
            </Button>
          ) : (
            <Button color="primary" style={{ fontSize: '14px', width: '200px' }} onClick={handleSubmit}>
              Modify Booking
            </Button>
          )}
        </Col>
      </ModalBody>
    </Modal>
  );
};

export default ModifyPlayerV2;

export function ModifyPreferredSlot({ isOpen, onClose, slotsDataObject, transformedSlotsData, selectedSlot, setSelectedSlot, intervalData }) {
  return (
    <>
      <Modal isOpen={isOpen} centered size="md">
        <ModalHeader toggle={onClose}>
          <h4>Modify Preferred Slot</h4>
        </ModalHeader>

        <ModalBody>
          <Row>
            {slotsDataObject && (
              <Col md="12" className="mb-3">
                <div className="select-slot-container">
                  <div className="slot-range-picker-container">
                    <FromAndToRangePicker
                      slotData={transformedSlotsData}
                      selectedSlot={selectedSlot}
                      setSelectedSlot={setSelectedSlot}
                      selectedDate={''}
                      intervalData={intervalData}
                    />
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </ModalBody>

        <ModalFooter className="d-flex justify-content-center">
          <Button className="overlay-disable" color="primary" onClick={onClose}>
            Submit
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
